// Pure helpers for the Forms screens — no firebase imports so this bundles into the
// node unit harness (scripts/utest-forms.mjs). Firestore glue lives in formsData.js.

const dayKey = (d) => d.toISOString().slice(0, 10)

/**
 * Aggregate daily formStats docs over a trailing window ending today (inclusive).
 * stats: [{ date:'YYYY-MM-DD', views, submissions, spam }]. Conversion is
 * submissions/views, or null when there are no views (UI renders "—"). Spam is
 * excluded from conversion (it never lands in `submissions`). dailyBars is one
 * zero-filled bucket per day, oldest -> newest, charting daily submissions.
 */
export function formAnalytics(stats, rangeDays, today = new Date()) {
  const byDate = new Map()
  for (const s of stats || []) byDate.set(s.date, s)
  const end = new Date(today)
  let views = 0, submissions = 0, spam = 0
  const dailyBars = []
  for (let i = rangeDays - 1; i >= 0; i--) {
    const d = new Date(end)
    d.setDate(d.getDate() - i)
    const key = dayKey(d)
    const row = byDate.get(key)
    const subs = row ? (row.submissions || 0) : 0
    views += row ? (row.views || 0) : 0
    submissions += subs
    spam += row ? (row.spam || 0) : 0
    dailyBars.push({ label: String(d.getDate()), count: subs })
  }
  const conversion = views > 0 ? submissions / views : null
  return { views, submissions, spam, conversion, dailyBars }
}

export function submissionReviewState(sub) {
  if (sub?.spam?.verdict !== 'suspect') return 'real'
  if (sub?.review?.action === 'released') return 'released'
  if (sub?.review?.action === 'kept') return 'kept'
  return 'needs-review'
}

export function submissionName(sub) {
  const v = (sub && sub.values) || {}
  const first = v.firstname || v.firstName || ''
  const last = v.lastname || v.lastName || ''
  const full = `${first} ${last}`.trim()
  if (full) return full
  if (v.email) return String(v.email)
  return '—'
}

/**
 * Friendly copy for a failed promoteSubmission callable. When the function is not
 * deployed yet the callable resolves as not-found/internal/unavailable — steer the
 * user to the manual deploy instead of showing a raw error (design §6.4).
 */
export function promoteErrorMessage(err) {
  const code = String((err && err.code) || '').replace(/^functions\//, '')
  const msg = String((err && err.message) || '')
  if (code === 'failed-precondition' && /validation/i.test(msg)) {
    return "This submission no longer passes the form's validation, so it cannot be released."
  }
  if (code === 'failed-precondition') return msg || 'This submission cannot be released.'
  if (code === 'permission-denied' || code === 'unauthenticated') return 'You do not have permission to do that.'
  if (code === 'not-found' || code === 'internal' || code === 'unavailable' || code === '') {
    return 'This action needs the forms function deployed. Ask an admin to deploy promoteSubmission, then try again.'
  }
  return msg || 'The action could not be completed.'
}
